/**
 * useHubNavigation — extracts the mode + doc-selection + direction
 * state that previously lived inline in LibraryHubSlideOver.
 *
 * Responsibilities:
 *   - Tracks innerMode (HubMode) and selectedDocId (string | null).
 *   - Derives transition direction: "forward" when drilling in
 *     (library-list → list → detail), "backward" on back-stack pop.
 *   - Exposes the computed BackTarget so HubNavShell can render the
 *     back affordance without reimplementing the back-stack logic.
 *   - Re-syncs to initialMode each time `open` flips to true (Spec
 *     34: the ``mode`` prop resets on every re-open). Clears
 *     selectedDocId on close.
 *
 * Pure state — zero DOM, zero side-effects beyond standard React
 * hooks. Safe to test with renderHook().
 */

import { useCallback, useEffect, useState } from "react";
import type { HubMode } from "../components/LibraryHubSlideOver";

export type NavDirection = "forward" | "backward";

export interface BackTarget {
  kind: "back" | "cancel";
  label: string;
  ariaLabel: string;
  to: HubMode;
}

function backTargetFor(mode: HubMode): BackTarget | null {
  switch (mode) {
    case "library-list":
      return null;
    case "empty":
      return null;
    case "uploading":
      return null;
    case "creating":
      return {
        kind: "cancel",
        label: "Cancel",
        ariaLabel: "Cancel new library",
        to: "library-list",
      };
    case "list":
      return {
        kind: "back",
        label: "Back",
        ariaLabel: "Back to libraries",
        to: "library-list",
      };
    case "detail":
      return {
        kind: "back",
        label: "Back",
        ariaLabel: "Back to documents",
        to: "list",
      };
  }
}

// Drill depth for each mode — higher number = deeper in the stack.
// Going from a lower to higher depth = forward transition.
const DEPTH: Record<HubMode, number> = {
  "library-list": 0,
  empty: 0,
  creating: 1,
  uploading: 1,
  list: 1,
  detail: 2,
};

export interface HubNavigationState {
  innerMode: HubMode;
  selectedDocId: string | null;
  direction: NavDirection;
  back: BackTarget | null;
  /** Navigate forward to a new mode (drill in). */
  navigateTo: (mode: HubMode) => void;
  /** Navigate backward; also clears selectedDocId when leaving detail. */
  navigateBack: () => void;
  /** Record the doc the writer clicked on, then navigate to detail. */
  selectDoc: (docId: string) => void;
}

export function useHubNavigation(initialMode: HubMode, open: boolean): HubNavigationState {
  const [innerMode, setInnerMode] = useState<HubMode>(initialMode);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [direction, setDirection] = useState<NavDirection>("forward");

  // Re-sync whenever the slide-over re-opens or the external mode
  // prop changes. The parent controls which mode to land on for
  // each open event (e.g. it may open directly on "list" if a
  // library is already selected).
  useEffect(() => {
    if (open) setInnerMode(initialMode);
    if (!open) setSelectedDocId(null);
  }, [open, initialMode]);

  const navigateTo = useCallback(
    (mode: HubMode) => {
      setDirection(DEPTH[mode] >= DEPTH[innerMode] ? "forward" : "backward");
      setInnerMode(mode);
    },
    [innerMode],
  );

  const navigateBack = useCallback(() => {
    const target = backTargetFor(innerMode);
    if (!target) return;
    if (innerMode === "detail") setSelectedDocId(null);
    setDirection("backward");
    setInnerMode(target.to);
  }, [innerMode]);

  const selectDoc = useCallback((docId: string) => {
    setSelectedDocId(docId);
    setDirection("forward");
    setInnerMode("detail");
  }, []);

  return {
    innerMode,
    selectedDocId,
    direction,
    back: backTargetFor(innerMode),
    navigateTo,
    navigateBack,
    selectDoc,
  };
}
